import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

interface ChoiceProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
  description?: ReactNode;
}

/** <Checkbox label="Remember me" {...register("remember")} /> */
export const Checkbox = forwardRef<HTMLInputElement, ChoiceProps>(function Checkbox({ label, description, className, id, ...props }, ref) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <label
      htmlFor={inputId}
      className={cn("group flex cursor-pointer items-start gap-3", props.disabled && "cursor-not-allowed opacity-60", className)}
    >
      <span className="relative mt-0.5 grid size-5 shrink-0 place-items-center">
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          className="peer absolute inset-0 cursor-pointer appearance-none rounded-md border border-line bg-elevated transition checked:border-primary checked:bg-[var(--primary-strong)] checked:shadow-glow-1 focus-visible:shadow-[var(--focus-ring)]"
          {...props}
        />
        <Check
          className="pointer-events-none relative size-3.5 text-white opacity-0 transition peer-checked:opacity-100"
          strokeWidth={3}
          aria-hidden
        />
      </span>
      <span className="flex flex-col">
        <span className="text-sm text-ink">{label}</span>
        {description && <span className="text-xs text-ink-muted">{description}</span>}
      </span>
    </label>
  );
});

/** <Radio name="delivery" value="pickup" label="Pickup" /> — group several with <RadioGroup>. */
export const Radio = forwardRef<HTMLInputElement, ChoiceProps>(function Radio({ label, description, className, id, ...props }, ref) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <label
      htmlFor={inputId}
      className={cn("flex cursor-pointer items-start gap-3", props.disabled && "cursor-not-allowed opacity-60", className)}
    >
      <span className="relative mt-0.5 grid size-5 shrink-0 place-items-center">
        <input
          ref={ref}
          id={inputId}
          type="radio"
          className="peer absolute inset-0 cursor-pointer appearance-none rounded-full border border-line bg-elevated transition checked:border-primary checked:shadow-glow-1 focus-visible:shadow-[var(--focus-ring)]"
          {...props}
        />
        <span
          className="pointer-events-none relative size-2.5 scale-0 rounded-full bg-cyan transition peer-checked:scale-100"
          aria-hidden
        />
      </span>
      <span className="flex flex-col">
        <span className="text-sm text-ink">{label}</span>
        {description && <span className="text-xs text-ink-muted">{description}</span>}
      </span>
    </label>
  );
});

/** <RadioGroup legend="Delivery">…<Radio/>…</RadioGroup> */
export function RadioGroup({ legend, children, className }: { legend: ReactNode; children: ReactNode; className?: string }) {
  return (
    <fieldset className={cn("flex flex-col gap-3", className)}>
      <legend className="mb-2 text-sm font-medium text-ink-soft">{legend}</legend>
      {children}
    </fieldset>
  );
}

interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
}

/** <Switch checked={on} onCheckedChange={setOn} label="Vacation mode" /> */
export function Switch({ checked, onCheckedChange, label, description, disabled, className }: SwitchProps) {
  const id = useId();
  return (
    <div className={cn("flex items-center justify-between gap-4", className)}>
      <div className="flex flex-col">
        <span id={`${id}-label`} className="text-sm text-ink">
          {label}
        </span>
        {description && <span className="text-xs text-ink-muted">{description}</span>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          "relative h-7 w-12 shrink-0 rounded-full border transition-colors duration-200 disabled:opacity-50",
          checked ? "border-primary bg-[var(--primary-strong)] shadow-glow-1" : "border-line bg-elevated",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 size-[22px] rounded-full bg-white shadow transition-transform duration-200",
            checked ? "translate-x-[22px]" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}
